import { IBus }                                            from '@/Emulator/Bus';
import { CpuFlag }                                         from '@/Emulator/Hardware/CPU/CpuFlag';
import { AddressingModePtr, CpuInstruction, OperationPtr } from '@/Emulator/Hardware/CPU/CpuInstruction';

/**
 * The Central Processing Unit of a NES console.
 */
export class CPU implements ISerializable
{
    private readonly instructions: CpuInstruction[] = new Array(256);
    private readonly instructionLog: string[]       = [];

    /**
     * The 8-bit accumulator.
     *
     * This is the primary math and logic workhorse. All arithmetic goes through
     * here.
     */
    private a: number = 0;

    /**
     * Index X. Used for loop counters and specific memory addressing offsets.
     */
    private x: number = 0;

    /**
     * Index Y. Same as X, used for advanced indirect memory indexing.
     */
    private y: number = 0;

    /**
     * The program counter. A pointer to the next memory address in the prgRom.
     */
    private pc: number = 0;

    /**
     * The stack pointer. Points to the current top of the stack in memory range
     * of $0100~01FF.
     */
    private sp: number = 0;

    /**
     * The status register represented as a single byte where individual bits
     * represent different condition flags.
     */
    private p: number = CpuFlag.Unused | CpuFlag.InterruptDisable;

    private isNmiRequested: boolean = false;
    private isIrqRequested: boolean = false;
    private absoluteAddress: number = 0x0000;
    private stalledCycles: number   = 0;

    constructor(private readonly bus: IBus)
    {
        this._defineInstructionSet([
            [0x00, 'BRK', 7, this.opBrk, this.modeImplied],
            [0x01, 'ORA', 6, this.opOra, this.modeIndirectX],
            [0x05, 'ORA', 3, this.opOra, this.modeZeroPage],
            [0x06, 'ASL', 5, this.opAsl, this.modeZeroPage],
            [0x08, 'PHP', 3, this.opPhp, this.modeImplied],
            [0x09, 'ORA', 2, this.opOra, this.modeImmediate],
            [0x0A, 'ASL', 2, this.opAslA, this.modeImplied],
            [0x0D, 'ORA', 4, this.opOra, this.modeAbsolute],
            [0x0E, 'ASL', 6, this.opAsl, this.modeAbsolute],
            [0x10, 'BPL', 2, this.opBpl, this.modeRelative],
            [0x11, 'ORA', 5, this.opOra, this.modeIndirectY],
            [0x15, 'ORA', 4, this.opOra, this.modeZeroPageX],
            [0x16, 'ASL', 6, this.opAsl, this.modeZeroPageX],
            [0x18, 'CLC', 2, this.opClc, this.modeImplied],
            [0x19, 'ORA', 4, this.opOra, this.modeAbsoluteY],
            [0x1D, 'ORA', 4, this.opOra, this.modeAbsoluteX],
            [0x1E, 'ASL', 7, this.opAsl, this.modeAbsoluteX],
            [0x20, 'JSR', 6, this.opJsr, this.modeAbsolute],
            [0x21, 'AND', 6, this.opAnd, this.modeIndirectX],
            [0x24, 'BIT', 3, this.opBit, this.modeZeroPage],
            [0x25, 'AND', 3, this.opAnd, this.modeZeroPage],
            [0x26, 'ROL', 5, this.opRol, this.modeZeroPage],
            [0x28, 'PLP', 4, this.opPlp, this.modeImplied],
            [0x29, 'AND', 2, this.opAnd, this.modeImmediate],
            [0x2A, 'ROL', 2, this.opRolA, this.modeImplied],
            [0x2C, 'BIT', 4, this.opBit, this.modeAbsolute],
            [0x2D, 'AND', 4, this.opAnd, this.modeAbsolute],
            [0x2E, 'ROL', 6, this.opRol, this.modeAbsolute],
            [0x30, 'BMI', 2, this.opBmi, this.modeRelative],
            [0x31, 'AND', 5, this.opAnd, this.modeIndirectY],
            [0x35, 'AND', 4, this.opAnd, this.modeZeroPageX],
            [0x36, 'ROL', 6, this.opRol, this.modeZeroPageX],
            [0x38, 'SEC', 2, this.opSec, this.modeImplied],
            [0x39, 'AND', 4, this.opAnd, this.modeAbsoluteY],
            [0x3D, 'AND', 4, this.opAnd, this.modeAbsoluteX],
            [0x3E, 'ROL', 7, this.opRol, this.modeAbsoluteX],
            [0x40, 'RTI', 6, this.opRti, this.modeImplied],
            [0x41, 'EOR', 6, this.opEor, this.modeIndirectX],
            [0x45, 'EOR', 3, this.opEor, this.modeZeroPage],
            [0x46, 'LSR', 5, this.opLsr, this.modeZeroPage],
            [0x48, 'PHA', 3, this.opPha, this.modeImplied],
            [0x49, 'EOR', 2, this.opEor, this.modeImmediate],
            [0x4A, 'LSR', 2, this.opLsrA, this.modeImplied],
            [0x4C, 'JMP', 3, this.opJmp, this.modeAbsolute],
            [0x4D, 'EOR', 4, this.opEor, this.modeAbsolute],
            [0x4E, 'LSR', 6, this.opLsr, this.modeAbsolute],
            [0x50, 'BVC', 2, this.opBvc, this.modeRelative],
            [0x51, 'EOR', 5, this.opEor, this.modeIndirectY],
            [0x55, 'EOR', 4, this.opEor, this.modeZeroPageX],
            [0x56, 'LSR', 6, this.opLsr, this.modeZeroPageX],
            [0x58, 'CLI', 2, this.opCli, this.modeImplied],
            [0x59, 'EOR', 4, this.opEor, this.modeAbsoluteY],
            [0x5D, 'EOR', 4, this.opEor, this.modeAbsoluteX],
            [0x5E, 'LSR', 7, this.opLsr, this.modeAbsoluteX],
            [0x60, 'RTS', 6, this.opRts, this.modeImplied],
            [0x61, 'ADC', 6, this.opAdc, this.modeIndirectX],
            [0x65, 'ADC', 3, this.opAdc, this.modeZeroPage],
            [0x66, 'ROR', 5, this.opRor, this.modeZeroPage],
            [0x68, 'PLA', 4, this.opPla, this.modeImplied],
            [0x69, 'ADC', 2, this.opAdc, this.modeImmediate],
            [0x6A, 'ROR', 2, this.opRorA, this.modeImplied],
            [0x6C, 'JMP', 5, this.opJmp, this.modeIndirect],
            [0x6D, 'ADC', 4, this.opAdc, this.modeAbsolute],
            [0x6E, 'ROR', 6, this.opRor, this.modeAbsolute],
            [0x70, 'BVS', 2, this.opBvs, this.modeRelative],
            [0x71, 'ADC', 5, this.opAdc, this.modeIndirectY],
            [0x75, 'ADC', 4, this.opAdc, this.modeZeroPageX],
            [0x76, 'ROR', 6, this.opRor, this.modeZeroPageX],
            [0x78, 'SEI', 2, this.opSei, this.modeImplied],
            [0x79, 'ADC', 4, this.opAdc, this.modeAbsoluteY],
            [0x7D, 'ADC', 4, this.opAdc, this.modeAbsoluteX],
            [0x7E, 'ROR', 7, this.opRor, this.modeAbsoluteX],
            [0x81, 'STA', 6, this.opSta, this.modeIndirectX],
            [0x84, 'STY', 3, this.opSty, this.modeZeroPage],
            [0x85, 'STA', 3, this.opSta, this.modeZeroPage],
            [0x86, 'STX', 3, this.opStx, this.modeZeroPage],
            [0x88, 'DEY', 2, this.opDey, this.modeImplied],
            [0x8A, 'TXA', 2, this.opTxa, this.modeImplied],
            [0x8C, 'STY', 4, this.opSty, this.modeAbsolute],
            [0x8D, 'STA', 4, this.opSta, this.modeAbsolute],
            [0x8E, 'STX', 4, this.opStx, this.modeAbsolute],
            [0x90, 'BCC', 2, this.opBcc, this.modeRelative],
            [0x91, 'STA', 6, this.opSta, this.modeIndirectY],
            [0x94, 'STY', 4, this.opSty, this.modeZeroPageX],
            [0x95, 'STA', 4, this.opSta, this.modeZeroPageX],
            [0x96, 'STX', 4, this.opStx, this.modeZeroPageY],
            [0x98, 'TYA', 2, this.opTya, this.modeImplied],
            [0x99, 'STA', 5, this.opSta, this.modeAbsoluteY],
            [0x9A, 'TXS', 2, this.opTxs, this.modeImplied],
            [0x9D, 'STA', 5, this.opSta, this.modeAbsoluteX],
            [0xA0, 'LDY', 2, this.opLdy, this.modeImmediate],
            [0xA1, 'LDA', 6, this.opLda, this.modeIndirectX],
            [0xA2, 'LDX', 2, this.opLdx, this.modeImmediate],
            [0xA4, 'LDY', 3, this.opLdy, this.modeZeroPage],
            [0xA5, 'LDA', 3, this.opLda, this.modeZeroPage],
            [0xA6, 'LDX', 3, this.opLdx, this.modeZeroPage],
            [0xA8, 'TAY', 2, this.opTay, this.modeImplied],
            [0xA9, 'LDA', 2, this.opLda, this.modeImmediate],
            [0xAA, 'TAX', 2, this.opTax, this.modeImplied],
            [0xAC, 'LDY', 4, this.opLdy, this.modeAbsolute],
            [0xAD, 'LDA', 4, this.opLda, this.modeAbsolute],
            [0xAE, 'LDX', 4, this.opLdx, this.modeAbsolute],
            [0xB0, 'BCS', 2, this.opBcs, this.modeRelative],
            [0xB1, 'LDA', 5, this.opLda, this.modeIndirectY],
            [0xB4, 'LDY', 4, this.opLdy, this.modeZeroPageX],
            [0xB5, 'LDA', 4, this.opLda, this.modeZeroPageX],
            [0xB6, 'LDX', 4, this.opLdx, this.modeZeroPageY],
            [0xB8, 'CLV', 2, this.opClv, this.modeImplied],
            [0xB9, 'LDA', 4, this.opLda, this.modeAbsoluteY],
            [0xBA, 'TSX', 2, this.opTsx, this.modeImplied],
            [0xBC, 'LDY', 4, this.opLdy, this.modeAbsoluteX],
            [0xBD, 'LDA', 4, this.opLda, this.modeAbsoluteX],
            [0xBE, 'LDX', 4, this.opLdx, this.modeAbsoluteY],
            [0xC0, 'CPY', 2, this.opCpy, this.modeImmediate],
            [0xC1, 'CMP', 6, this.opCmp, this.modeIndirectX],
            [0xC4, 'CPY', 3, this.opCpy, this.modeZeroPage],
            [0xC5, 'CMP', 3, this.opCmp, this.modeZeroPage],
            [0xC6, 'DEC', 5, this.opDec, this.modeZeroPage],
            [0xC8, 'INY', 2, this.opIny, this.modeImplied],
            [0xC9, 'CMP', 2, this.opCmp, this.modeImmediate],
            [0xCA, 'DEX', 2, this.opDex, this.modeImplied],
            [0xCC, 'CPY', 4, this.opCpy, this.modeAbsolute],
            [0xCD, 'CMP', 4, this.opCmp, this.modeAbsolute],
            [0xCE, 'DEC', 6, this.opDec, this.modeAbsolute],
            [0xD0, 'BNE', 2, this.opBne, this.modeRelative],
            [0xD1, 'CMP', 5, this.opCmp, this.modeIndirectY],
            [0xD5, 'CMP', 4, this.opCmp, this.modeZeroPageX],
            [0xD6, 'DEC', 6, this.opDec, this.modeZeroPageX],
            [0xD8, 'CLD', 2, this.opCld, this.modeImplied],
            [0xD9, 'CMP', 4, this.opCmp, this.modeAbsoluteY],
            [0xDD, 'CMP', 4, this.opCmp, this.modeAbsoluteX],
            [0xDE, 'DEC', 7, this.opDec, this.modeAbsoluteX],
            [0xE0, 'CPX', 2, this.opCpx, this.modeImmediate],
            [0xE1, 'SBC', 6, this.opSbc, this.modeIndirectX],
            [0xE4, 'CPX', 3, this.opCpx, this.modeZeroPage],
            [0xE5, 'SBC', 3, this.opSbc, this.modeZeroPage],
            [0xE6, 'INC', 5, this.opInc, this.modeZeroPage],
            [0xE8, 'INX', 2, this.opInx, this.modeImplied],
            [0xE9, 'SBC', 2, this.opSbc, this.modeImmediate],
            [0xEA, 'NOP', 2, this.opNop, this.modeImplied],
            [0xEC, 'CPX', 4, this.opCpx, this.modeAbsolute],
            [0xED, 'SBC', 4, this.opSbc, this.modeAbsolute],
            [0xEE, 'INC', 6, this.opInc, this.modeAbsolute],
            [0xF0, 'BEQ', 2, this.opBeq, this.modeRelative],
            [0xF1, 'SBC', 5, this.opSbc, this.modeIndirectY],
            [0xF5, 'SBC', 4, this.opSbc, this.modeZeroPageX],
            [0xF6, 'INC', 6, this.opInc, this.modeZeroPageX],
            [0xF8, 'SED', 2, this.opSed, this.modeImplied],
            [0xF9, 'SBC', 4, this.opSbc, this.modeAbsoluteY],
            [0xFD, 'SBC', 4, this.opSbc, this.modeAbsoluteX],
            [0xFE, 'INC', 7, this.opInc, this.modeAbsoluteX],
        ]);
    }

    public dump(): any
    {
        return {
            a:   this.a,
            x:   this.x,
            y:   this.y,
            pc:  this.pc,
            sp:  this.sp,
            p:   this.p,
            log: this.instructionLog,
        };
    }

    /**
     * Resets the CPU to its initial state, setting the program counter to the
     * address specified in the reset vector ($FFFC, $FFFD) and initializing
     * registers to their default values.
     */
    public reset(): void
    {
        this.a = 0x00;
        this.x = 0x00;
        this.y = 0x00;

        const lo: number = this.bus.read(0xFFFC);
        const hi: number = this.bus.read(0xFFFD);

        this.pc = (hi << 8) | lo;
        this.sp = 0xFD;

        this.p = CpuFlag.Unused | CpuFlag.InterruptDisable;
    }

    /**
     * Stalls the CPU for the given amount of cycles.
     */
    public stall(cycles: number): void
    {
        this.stalledCycles += cycles;
    }

    /**
     * Executes a single instruction that the program counter is pointing to and
     * returns the number of cycles consumed by this instruction.
     */
    public tick(): number
    {
        if (this.stalledCycles > 0) {
            const cycles: number = this.stalledCycles;
            this.stalledCycles   = 0;

            return cycles;
        }

        if (this.isNmiRequested) {
            this.isNmiRequested = false;
            return this.nmi();
        }

        if (this.isIrqRequested) {
            this.isIrqRequested     = false;
            const irqCycles: number = this.irq();

            if (irqCycles > 0) {
                return irqCycles;
            }
        }

        const opcode: number        = this.bus.read(this.pc++);
        const instr: CpuInstruction = this.instructions[opcode];

        if (!instr) {
            throw new Error(`Unknown instruction: ${opcode.toString(16).padStart(2, '0')}`);
        }

        const previousState: State = {
            a:  this.a,
            x:  this.x,
            y:  this.y,
            p:  this.p,
            pc: this.pc,
            sp: this.sp,
        };

        const modeCycles: number = instr.addressMode();
        const opCycles: number   = instr.operation();

        return instr.cycles + modeCycles + opCycles;
    }

    /**
     * Schedules a non-maskable interrupt to be executed on the next
     * {@link tick}.
     */
    public scheduleNmi(): void
    {
        this.isNmiRequested = true;
    }

    /**
     * Schedules an interrupt request to be executed on the next {@link tick}.
     */
    public scheduleIrq(): void
    {
        this.isIrqRequested = true;
    }

    public serialize(): Record<string, any>
    {
        return {
            a:               this.a,
            x:               this.x,
            y:               this.y,
            p:               this.p,
            sp:              this.sp,
            pc:              this.pc,
            isNmiRequested:  this.isNmiRequested,
            isIrqRequested:  this.isIrqRequested,
            absoluteAddress: this.absoluteAddress,
            stalledCycles:   this.stalledCycles,
        };
    }

    public deserialize(data: Record<string, any>): void
    {
        this.a               = data.a;
        this.x               = data.x;
        this.y               = data.y;
        this.p               = data.p;
        this.sp              = data.sp;
        this.pc              = data.pc;
        this.isNmiRequested  = data.isNmiRequested;
        this.isIrqRequested  = data.isIrqRequested;
        this.absoluteAddress = data.absoluteAddress;
        this.stalledCycles   = data.stalledCycles;
    }

    /**
     * Forces the CPU to halt its current timeline and jump to the hardware
     * Non-Maskable Interrupt (NMI) vector.
     *
     * Returns the amount of additional cycles performed.
     */
    private nmi(): number
    {
        this.interrupt(0xFFFA, 0xFFFB);

        return 7;
    }

    /**
     * Forces the CPU to intercept execution and branch to the hardware
     * Interrupt Request (IRQ) vector, provided interrupts are not disabled.
     *
     * Returns the amount of additional cycles performed.
     */
    private irq(): number
    {
        // Hardware IRQs are ignored if the Interrupt Disable ('I') flag is set
        if (this.getFlag(CpuFlag.InterruptDisable)) {
            return 0;
        }

        this.interrupt(0xFFFE, 0xFFFF);

        // A regular IRQ takes 7 cycles.
        return 7;
    }

    private interrupt(loAddress: number, hiAddress: number): void
    {
        this.bus.write(0x0100 + this.sp--, (this.pc >> 8) & 0xFF);
        this.bus.write(0x0100 + this.sp--, this.pc & 0xFF);
        this.bus.write(0x0100 + this.sp--, (this.p & ~CpuFlag.Break) | CpuFlag.Unused);

        this.setFlag(CpuFlag.InterruptDisable, true);

        const lo: number = this.bus.read(loAddress);
        const hi: number = this.bus.read(hiAddress);

        this.pc = (hi << 8) | lo;
    }

    /**
     * Toggles a specific flag in the status register based on the provided
     * condition.
     *
     * @param flag - The {@link CpuFlag} to set a condition for.
     * @param condition - The condition for the provided flag.
     * @private
     */
    private setFlag(flag: CpuFlag, condition: boolean): void
    {
        if (condition) {
            this.p |= flag;
        } else {
            this.p &= ~flag;
        }
    }

    /**
     * Returns true if a condition for the give {@link CpuFlag} is true, false
     * otherwise.
     *
     * @param flag - The {@link CpuFlag} to retrieve the current condition for.
     * @private
     */
    private getFlag(flag: CpuFlag): boolean
    {
        return (this.p & flag) !== 0;
    }

    // region ---------------------------------------------- Instruction Set ---

    // region === System Operations ===

    /**
     * BRK: Software Break / Force Interrupt
     *
     * Simulates a hardware interrupt triggered via software.
     */
    private readonly opBrk: OperationPtr = (): number => {
        // The 6502 architecture advances the PC past a padding byte during a BRK instruction
        this.pc++;

        // Push 16-bit program counter (High byte then Low byte) to stack page $0100
        this.bus.write(0x0100 + this.sp--, (this.pc >> 8) & 0xFF);
        this.bus.write(0x0100 + this.sp--, this.pc & 0xFF);

        // Push status flags with the Break flag (Bit 4) explicitly set
        this.bus.write(0x0100 + this.sp--, this.p | CpuFlag.Break | CpuFlag.Unused);

        // Disable incoming hardware interrupts
        this.setFlag(CpuFlag.InterruptDisable, true);

        // Divert execution stream to the absolute Interrupt Vector ($FFFE/$FFFF)
        const lo: number = this.bus.read(0xFFFE);
        const hi: number = this.bus.read(0xFFFF);

        this.pc = (hi << 8) | lo;

        return 0;
    };

    /**
     * SEI: Set Interrupt Disable
     *
     * Sets the 'I' flag to 1, blocking IRQs.
     */
    private readonly opSei: OperationPtr = (): number => {
        this.setFlag(CpuFlag.InterruptDisable, true);

        return 0;
    };

    /**
     * RTI: Return from Interrupt
     *
     * Pulls the flag byte and execution address back out of the stack.
     */
    private readonly opRti: OperationPtr = (): number => {
        // Restore status register from stack, ignoring bit 4 and pinning bit 5 to 1
        const flags: number = this.bus.read(0x0100 + ++this.sp);
        this.p              = (flags & ~CpuFlag.Break) | CpuFlag.Unused;

        // Pull 16-bit Program Counter back out of stack memory
        const lo = this.bus.read(0x0100 + ++this.sp);
        const hi = this.bus.read(0x0100 + ++this.sp);

        this.pc = (hi << 8) | lo;

        return 0;
    };

    /**
     * RTS: Return from subroutine.
     */
    private readonly opRts: OperationPtr = (): number => {
        const lo: number = this.bus.read(0x0100 + ++this.sp);
        const hi: number = this.bus.read(0x0100 + ++this.sp);

        this.pc = ((hi << 8) | lo) + 1;

        return 0;
    };

    /**
     * BIT: Bit Test - Tests bits in memory against the Accumulator.
     *
     * @remarks The Zero flag is updated via an "AND" result, but Negative and
     *          Overflow are instantly overwritten with raw bits 7 and 6 of the
     *          target byte.
     */
    private opBit = (): number => {
        const value = this.bus.read(this.absoluteAddress);

        this.setFlag(CpuFlag.Zero, (this.a & value) === 0x00);
        this.setFlag(CpuFlag.Negative, (value & 0x80) !== 0);
        this.setFlag(CpuFlag.Overflow, (value & 0x40) !== 0);

        return 0;
    };

    // endregion

    // region === Memory Operations ===

    /**
     * LDA: Load Accumulator
     *
     * Pulls a byte from memory into register A and sets Zero/Negative flags
     * based on value.
     */
    private readonly opLda: OperationPtr = (): number => {
        const value: number = this.bus.read(this.absoluteAddress);
        this.a              = value & 0xFF;

        this.setFlag(CpuFlag.Zero, this.a === 0x00);
        this.setFlag(CpuFlag.Negative, (this.a & 0x80) !== 0);

        return 0;
    };

    /**
     * STA: Store Accumulator
     */
    private readonly opSta: OperationPtr = (): number => {
        this.bus.write(this.absoluteAddress, this.a);

        return 0;
    };

    /**
     * LDX: Load X Register
     */
    private readonly opLdx: OperationPtr = (): number => {
        const value: number = this.bus.read(this.absoluteAddress);
        this.x              = value & 0xFF;

        this.setFlag(CpuFlag.Zero, this.x === 0x00);
        this.setFlag(CpuFlag.Negative, (this.x & 0x80) !== 0);

        return 0;
    };

    /**
     * STX: Store X Register to Memory
     */
    private readonly opStx: OperationPtr = (): number => {
        this.bus.write(this.absoluteAddress, this.x);

        return 0;
    };

    /**
     * LDY: Load Y Register
     */
    private readonly opLdy: OperationPtr = (): number => {
        const value: number = this.bus.read(this.absoluteAddress);
        this.y              = value & 0xFF;

        this.setFlag(CpuFlag.Zero, this.y === 0x00);
        this.setFlag(CpuFlag.Negative, (this.y & 0x80) !== 0);

        return 0;
    };

    /**
     * STY: Store Y Register to Memory
     */
    private readonly opSty: OperationPtr = (): number => {
        this.bus.write(this.absoluteAddress, this.y);

        return 0;
    };

    // endregion

    // region === Bitwise Operations ===

    /**
     * AND: Bitwise AND with Accumulator
     */
    private readonly opAnd: OperationPtr = (): number => {
        this.a &= this.bus.read(this.absoluteAddress);

        this.setFlag(CpuFlag.Zero, this.a === 0x00);
        this.setFlag(CpuFlag.Negative, (this.a & 0x80) !== 0);

        return 0;
    };

    /**
     * ORA: Bitwise OR with Accumulator
     */
    private readonly opOra: OperationPtr = (): number => {
        this.a |= this.bus.read(this.absoluteAddress);

        this.setFlag(CpuFlag.Zero, this.a === 0x00);
        this.setFlag(CpuFlag.Negative, (this.a & 0x80) !== 0);

        return 0;
    };

    /**
     * EOR: Bitwise Exclusive OR with Accumulator
     */
    private readonly opEor: OperationPtr = (): number => {
        this.a ^= this.bus.read(this.absoluteAddress);

        this.setFlag(CpuFlag.Zero, this.a === 0x00);
        this.setFlag(CpuFlag.Negative, (this.a & 0x80) !== 0);

        return 0;
    };

    /**
     * ASL: Arithmetic Shift Left (Memory Target)
     */
    private readonly opAsl: OperationPtr = (): number => {
        const value: number = this.bus.read(this.absoluteAddress);
        this.setFlag(CpuFlag.Carry, (value & 0x80) !== 0);

        const newValue: number = (value << 1) & 0xFF;
        this.bus.write(this.absoluteAddress, newValue);

        this.setFlag(CpuFlag.Zero, newValue === 0x00);
        this.setFlag(CpuFlag.Negative, (newValue & 0x80) !== 0);

        return 0;
    };

    /**
     * ASL A: Arithmetic Shift Left (Accumulator)
     */
    private readonly opAslA: OperationPtr = (): number => {
        this.setFlag(CpuFlag.Carry, (this.a & 0x80) !== 0);

        this.a = (this.a << 1) & 0xFF;

        this.setFlag(CpuFlag.Zero, this.a === 0x00);
        this.setFlag(CpuFlag.Negative, (this.a & 0x80) !== 0);

        return 0;
    };

    /**
     * LSR: Logical Shift Right (Memory Target)
     */
    private readonly opLsr: OperationPtr = (): number => {
        const value: number = this.bus.read(this.absoluteAddress);
        this.setFlag(CpuFlag.Carry, (value & 0x01) !== 0);

        const newValue: number = value >>> 1;
        this.bus.write(this.absoluteAddress, newValue);

        this.setFlag(CpuFlag.Zero, newValue === 0x00);
        this.setFlag(CpuFlag.Negative, false);

        return 0;
    };

    /**
     * LSR A: Logical Shift Right (Accumulator)
     */
    private readonly opLsrA: OperationPtr = (): number => {
        this.setFlag(CpuFlag.Carry, (this.a & 0x01) !== 0);

        this.a >>>= 1; // Unsigned right shift

        this.setFlag(CpuFlag.Zero, this.a === 0x00);
        this.setFlag(CpuFlag.Negative, false); // Shifting right always pulls a 0 into bit 7

        return 0;
    };

    /**
     * ROL: Rotate Left (Memory Target)
     */
    private readonly opRol: OperationPtr = (): number => {
        const value: number   = this.bus.read(this.absoluteAddress);
        const oldCarry: 0 | 1 = this.getFlag(CpuFlag.Carry) ? 1 : 0;
        this.setFlag(CpuFlag.Carry, (value & 0x80) !== 0);

        const newValue: number = ((value << 1) | oldCarry) & 0xFF;
        this.bus.write(this.absoluteAddress, newValue);

        this.setFlag(CpuFlag.Zero, newValue === 0x00);
        this.setFlag(CpuFlag.Negative, (newValue & 0x80) !== 0);

        return 0;
    };

    /**
     * ROL A: Rotate Left (Accumulator)
     * Shifts bit 7 into Carry, and moves the old Carry into bit 0.
     */
    private readonly opRolA: OperationPtr = (): number => {
        const oldCarry: 0 | 1 = this.getFlag(CpuFlag.Carry) ? 1 : 0;

        this.setFlag(CpuFlag.Carry, (this.a & 0x80) !== 0);
        this.a = ((this.a << 1) | oldCarry) & 0xFF;

        this.setFlag(CpuFlag.Zero, this.a === 0x00);
        this.setFlag(CpuFlag.Negative, (this.a & 0x80) !== 0);

        return 0;
    };

    /**
     * ROR: Rotate Right (Memory Target)
     */
    private readonly opRor: OperationPtr = (): number => {
        const value: number     = this.bus.read(this.absoluteAddress);
        const oldCarry: 0 | 128 = this.getFlag(CpuFlag.Carry) ? 0x80 : 0;
        this.setFlag(CpuFlag.Carry, (value & 0x01) !== 0);

        const newValue: number = (value >>> 1) | oldCarry;
        this.bus.write(this.absoluteAddress, newValue);

        this.setFlag(CpuFlag.Zero, newValue === 0x00);
        this.setFlag(CpuFlag.Negative, (newValue & 0x80) !== 0);

        return 0;
    };

    /**
     * ROR A: Rotate Right (Accumulator)
     * Shifts bit 0 into Carry, and moves the old Carry into bit 7.
     */
    private readonly opRorA: OperationPtr = (): number => {
        const oldCarry: 0 | 128 = this.getFlag(CpuFlag.Carry) ? 0x80 : 0;

        this.setFlag(CpuFlag.Carry, (this.a & 0x01) !== 0);
        this.a = (this.a >>> 1) | oldCarry;

        this.setFlag(CpuFlag.Zero, this.a === 0x00);
        this.setFlag(CpuFlag.Negative, (this.a & 0x80) !== 0);

        return 0;
    };

    // endregion

    // region === Arithmetic Operations ===

    /**
     * ADC: Add with Carry
     *
     * Handles standard unsigned carry flags and signed overflow flags
     * simultaneously.
     */
    private readonly opAdc: OperationPtr = (): number => {
        const value: number  = this.bus.read(this.absoluteAddress);
        const carry: 0 | 1   = this.getFlag(CpuFlag.Carry) ? 1 : 0;
        const result: number = this.a + value + carry;

        // Carry flag is set if the result exceeds 8-bit capacity (255)
        this.setFlag(CpuFlag.Carry, result > 0xFF);
        this.setFlag(CpuFlag.Zero, (result & 0xFF) === 0x00);

        // Signed Overflow occurs if adding two numbers with matching signs
        // yields a result with an inverted sign bit (bit 7).
        this.setFlag(CpuFlag.Overflow, ((~(this.a ^ value) & (this.a ^ result)) & 0x80) !== 0);
        this.setFlag(CpuFlag.Negative, (result & 0x80) !== 0);

        this.a = result & 0xFF;

        return 0;
    };

    /**
     * SBC: Subtract with Carry
     */
    private readonly opSbc: OperationPtr = (): number => {
        const value: number    = this.bus.read(this.absoluteAddress);
        const inverted: number = value ^ 0xFF;
        const carry: 0 | 1     = this.getFlag(CpuFlag.Carry) ? 1 : 0;
        const result: number   = this.a + inverted + carry;

        this.setFlag(CpuFlag.Carry, result > 0xFF);
        this.setFlag(CpuFlag.Zero, (result & 0xFF) === 0x00);
        this.setFlag(CpuFlag.Overflow, ((~(this.a ^ inverted) & (this.a ^ result)) & 0x80) !== 0);
        this.setFlag(CpuFlag.Negative, (result & 0x80) !== 0);

        this.a = result & 0xFF;

        return 0;
    };

    /**
     * INX: Increment Index X
     */
    private readonly opInx: OperationPtr = (): number => {
        this.x = (this.x + 1) & 0xFF;

        this.setFlag(CpuFlag.Zero, this.x === 0x00);
        this.setFlag(CpuFlag.Negative, (this.x & 0x80) !== 0);

        return 0;
    };

    /**
     * DEX: Decrement Index X
     */
    private readonly opDex: OperationPtr = (): number => {
        this.x = (this.x - 1) & 0xFF;

        this.setFlag(CpuFlag.Zero, this.x === 0x00);
        this.setFlag(CpuFlag.Negative, (this.x & 0x80) !== 0);

        return 0;
    };

    /**
     * INY: Increment Index Y
     */
    private readonly opIny: OperationPtr = (): number => {
        this.y = (this.y + 1) & 0xFF;

        this.setFlag(CpuFlag.Zero, this.y === 0x00);
        this.setFlag(CpuFlag.Negative, (this.y & 0x80) !== 0);

        return 0;
    };

    /**
     * DEY: Decrement Index Y
     */
    private readonly opDey: OperationPtr = (): number => {
        this.y = (this.y - 1) & 0xFF;

        this.setFlag(CpuFlag.Zero, this.y === 0x00);
        this.setFlag(CpuFlag.Negative, (this.y & 0x80) !== 0);

        return 0;
    };

    /**
     * INC: Increment the value at the current memory address.
     */
    private readonly opInc: OperationPtr = (): number => {
        const value: number = (this.bus.read(this.absoluteAddress) + 1) & 0xFF;

        this.bus.write(this.absoluteAddress, value);

        this.setFlag(CpuFlag.Zero, value === 0x00);
        this.setFlag(CpuFlag.Negative, (value & 0x80) !== 0);

        return 0;
    };

    /**
     * DEC: Decrement the value at the current memory address.
     */
    private readonly opDec: OperationPtr = (): number => {
        const value: number = (this.bus.read(this.absoluteAddress) - 1) & 0xFF;

        this.bus.write(this.absoluteAddress, value);

        this.setFlag(CpuFlag.Zero, value === 0x00);
        this.setFlag(CpuFlag.Negative, (value & 0x80) !== 0);

        return 0;
    };

    // endregion

    // region === Branching Operations ===

    /**
     * BEQ: Branch if Equal (Branch if Zero Flag == 1)
     */
    private readonly opBeq: OperationPtr = (): number => {
        if (!this.getFlag(CpuFlag.Zero)) {
            return 0;
        }

        const oldPc: number = this.pc;
        this.pc             = this.absoluteAddress;

        return (oldPc & 0xFF00) !== (this.pc & 0xFF00) ? 2 : 1;
    };

    /**
     * BNE: Branch if Not Equal (Branch if Zero Flag == 0)
     */
    private readonly opBne: OperationPtr = (): number => {
        if (this.getFlag(CpuFlag.Zero)) {
            return 0;
        }

        const oldPc: number = this.pc;
        this.pc             = this.absoluteAddress;

        return (oldPc & 0xFF00) !== (this.pc & 0xFF00) ? 2 : 1;
    };

    /**
     * BPL: Branch if Positive (Branch if Negative Flag == 0)
     */
    private readonly opBpl: OperationPtr = (): number => {
        if (this.getFlag(CpuFlag.Negative)) {
            return 0;
        }

        const oldPc: number = this.pc;
        this.pc             = this.absoluteAddress;

        return (oldPc & 0xFF00) !== (this.pc & 0xFF00) ? 2 : 1;
    };

    /**
     * BMI: Branch if Minus (Branch if Negative Flag == 1)
     */
    private readonly opBmi: OperationPtr = (): number => {
        if (!this.getFlag(CpuFlag.Negative)) {
            return 0;
        }

        const oldPc: number = this.pc;
        this.pc             = this.absoluteAddress;

        return ((oldPc & 0xFF00) !== (this.pc & 0xFF00)) ? 2 : 1;
    };

    /**
     * BCC: Branch if Carry Clear (Branch if Carry Flag == 0)
     */
    private readonly opBcc: OperationPtr = (): number => {
        if (this.getFlag(CpuFlag.Carry)) {
            return 0;
        }

        const oldPc: number = this.pc;
        this.pc             = this.absoluteAddress;

        return ((oldPc & 0xFF00) !== (this.pc & 0xFF00)) ? 2 : 1;
    };

    /**
     * BCS: Branch if Carry Set (Branch if Carry Flag == 1)
     */
    private readonly opBcs: OperationPtr = (): number => {
        if (!this.getFlag(CpuFlag.Carry)) {
            return 0;
        }

        const oldPc: number = this.pc;
        this.pc             = this.absoluteAddress;

        return ((oldPc & 0xFF00) !== (this.pc & 0xFF00)) ? 2 : 1;
    };

    /**
     * BVC: Branch if Overflow Clear (Branch if Overflow Flag == 0)
     */
    private readonly opBvc: OperationPtr = (): number => {
        if (this.getFlag(CpuFlag.Overflow)) {
            return 0;
        }

        const oldPc: number = this.pc;
        this.pc             = this.absoluteAddress;

        return ((oldPc & 0xFF00) !== (this.pc & 0xFF00)) ? 2 : 1;
    };

    /**
     * BVS: Branch if Overflow Set (Branch if Overflow Flag == 1)
     */
    private readonly opBvs: OperationPtr = (): number => {
        if (!this.getFlag(CpuFlag.Overflow)) {
            return 0;
        }

        const oldPc: number = this.pc;
        this.pc             = this.absoluteAddress;

        return ((oldPc & 0xFF00) !== (this.pc & 0xFF00)) ? 2 : 1;
    };

    // endregion

    // region === Comparison Operations ===

    /**
     * CMP: Compare Accumulator with Memory
     */
    private readonly opCmp: OperationPtr = (): number => {
        const value: number  = this.bus.read(this.absoluteAddress);
        const result: number = (this.a - value) & 0xFF;

        this.setFlag(CpuFlag.Carry, this.a >= value);
        this.setFlag(CpuFlag.Zero, this.a === value);
        this.setFlag(CpuFlag.Negative, (result & 0x80) !== 0);

        return 0;
    };

    /**
     * CPX: Compare X Register with Memory
     */
    private readonly opCpx: OperationPtr = (): number => {
        const value: number  = this.bus.read(this.absoluteAddress);
        const result: number = (this.x - value) & 0xFF;

        this.setFlag(CpuFlag.Carry, this.x >= value);
        this.setFlag(CpuFlag.Zero, this.x === value);
        this.setFlag(CpuFlag.Negative, (result & 0x80) !== 0);

        return 0;
    };

    /**
     * CPY: Compare Y Register with Memory
     */
    private readonly opCpy: OperationPtr = (): number => {
        const value: number  = this.bus.read(this.absoluteAddress);
        const result: number = (this.y - value) & 0xFF;

        this.setFlag(CpuFlag.Carry, this.y >= value);
        this.setFlag(CpuFlag.Zero, this.y === value);
        this.setFlag(CpuFlag.Negative, (result & 0x80) !== 0);

        return 0;
    };

    // endregion

    // region === Jump Operations ===

    /**
     * JMP: Jump to Location
     */
    private readonly opJmp: OperationPtr = (): number => {
        this.pc = this.absoluteAddress;

        return 0;
    };

    /**
     * JSR: Jump to Subroutine (Push return address minus 1 to stack)
     */
    private readonly opJsr: OperationPtr = (): number => {
        const returnAddr: number = this.pc - 1;

        // Push High byte then Low byte to Stack Page $0100
        this.bus.write(0x0100 + this.sp--, (returnAddr >> 8) & 0xFF);
        this.bus.write(0x0100 + this.sp--, returnAddr & 0xFF);

        this.pc = this.absoluteAddress;

        return 0;
    };

    // endregion

    // region === Flag Operations ===

    /**
     * CLC: Clear Carry Flag
     */
    private readonly opClc: OperationPtr = (): number => {
        this.setFlag(CpuFlag.Carry, false);

        return 0;
    };

    /**
     * SEC: Set Carry Flag
     */
    private readonly opSec: OperationPtr = (): number => {
        this.setFlag(CpuFlag.Carry, true);

        return 0;
    };

    /**
     * CLD: Clear Decimal Mode
     */
    private readonly opCld: OperationPtr = (): number => {
        this.setFlag(CpuFlag.DecimalMode, false);

        return 0;
    };

    /**
     * SED: Set Decimal Mode
     */
    private readonly opSed: OperationPtr = (): number => {
        this.setFlag(CpuFlag.DecimalMode, true);

        return 0;
    };

    /**
     * CLI: Clear Interrupt Disable
     */
    private readonly opCli: OperationPtr = (): number => {
        this.setFlag(CpuFlag.InterruptDisable, false);

        return 0;
    };

    /**
     * CLV: Clear Overflow Flag
     */
    private readonly opClv: OperationPtr = (): number => {
        this.setFlag(CpuFlag.Overflow, false);

        return 0;
    };

    // endregion

    // region === Register Transfers ===

    private readonly opTax: OperationPtr = (): number => {
        this.x = this.a;

        this.setFlag(CpuFlag.Zero, this.x === 0x00);
        this.setFlag(CpuFlag.Negative, (this.x & 0x80) !== 0);

        return 0;
    };

    private readonly opTxa: OperationPtr = (): number => {
        this.a = this.x;

        this.setFlag(CpuFlag.Zero, this.a === 0x00);
        this.setFlag(CpuFlag.Negative, (this.a & 0x80) !== 0);

        return 0;
    };

    private readonly opTay: OperationPtr = (): number => {
        this.y = this.a;

        this.setFlag(CpuFlag.Zero, this.y === 0x00);
        this.setFlag(CpuFlag.Negative, (this.y & 0x80) !== 0);

        return 0;
    };

    private readonly opTya: OperationPtr = (): number => {
        this.a = this.y;

        this.setFlag(CpuFlag.Zero, this.a === 0x00);
        this.setFlag(CpuFlag.Negative, (this.a & 0x80) !== 0);

        return 0;
    };

    private readonly opTsx: OperationPtr = (): number => {
        this.x = this.sp;

        this.setFlag(CpuFlag.Zero, this.x === 0x00);
        this.setFlag(CpuFlag.Negative, (this.x & 0x80) !== 0);

        return 0;
    };

    /**
     * TXS: Transfer X to Stack Pointer
     */
    private readonly opTxs: OperationPtr = (): number => {
        this.sp = this.x & 0xFF;

        return 0;
    };

    // endregion

    // region === Stack Operations ===

    private readonly opPha: OperationPtr = (): number => {
        this.bus.write(0x0100 + this.sp--, this.a);

        return 0;
    };

    private readonly opPhp: OperationPtr = (): number => {
        // Hardware Quirk: PHP always pushes the status byte with the Break flag
        //                 (Bit 4) and Unused flag (Bit 5) explicitly set to 1.
        this.bus.write(0x0100 + this.sp--, this.p | CpuFlag.Break | CpuFlag.Unused);

        return 0;
    };

    private readonly opPla: OperationPtr = (): number => {
        this.a = this.bus.read(0x0100 + ++this.sp);

        this.setFlag(CpuFlag.Zero, this.a === 0x00);
        this.setFlag(CpuFlag.Negative, (this.a & 0x80) !== 0);

        return 0;
    };

    private readonly opPlp: OperationPtr = (): number => {
        const value: number = this.bus.read(0x0100 + ++this.sp);

        // Hardware Quirk: PLP pulls the byte but completely ignores Bit 4,
        //                 and hard-forces Bit 5 (Unused) to stay 1.
        this.p = (value & ~CpuFlag.Break) | CpuFlag.Unused;

        return 0;
    };

    // endregion

    // region === Miscellaneous Operations ===

    /**
     * NOP: No Operation
     */
    private readonly opNop: OperationPtr = (): number => {
        return 0; // Pure passive cycle burners
    };

    // endregion

    // endregion

    // region --------------------------------------------- Addressing Modes ---

    /**
     * Immediate Mode: Increments the program counter by and uses the result
     *                 as the operand address.
     */
    private modeImmediate: AddressingModePtr = (): number => {
        this.absoluteAddress = this.pc++;

        return 0;
    };

    /**
     * Absolute Mode: Fetches a full 16-bit address from the next two bytes
     *                in memory (little-endian).
     */
    private modeAbsolute: AddressingModePtr = (): number => {
        const lo: number     = this.bus.read(this.pc++);
        const hi: number     = this.bus.read(this.pc++);
        this.absoluteAddress = (hi << 8) | lo;
        return 0;
    };

    /**
     * ZeroPage Mode: Fetches an 8-bit address from the next byte in memory.
     *                Allows for faster access to the first 256 bytes of RAM.
     */
    private modeZeroPage: AddressingModePtr = (): number => {
        this.absoluteAddress = this.bus.read(this.pc++) & 0x00FF;
        return 0;
    };

    /**
     * Implied Mode: The instruction has no operand; the target is implied by
     *               the instruction itself (e.g., CLC, INX).
     */
    private modeImplied: AddressingModePtr = (): number => {
        this.absoluteAddress = 0x0000;
        return 0;
    };

    /**
     * Relative Mode: Used exclusively by branch instructions.
     *
     * Fetches a signed 8-bit offset (-128 to +127) to move the Program Counter
     * forward or backward.
     */
    private modeRelative: AddressingModePtr = (): number => {
        const offset         = this.bus.read(this.pc++);
        // Convert unsigned 8-bit byte to signed JavaScript integer
        const signedOffset   = offset & 0x80 ? offset - 256 : offset;
        this.absoluteAddress = this.pc + signedOffset;
        return 0;
    };

    /**
     * Indirect Mode: Used exclusively by JMP ($XXXX).
     *
     * The next two bytes point to a memory location that holds the ACTUAL
     * target address.
     */
    private modeIndirect: AddressingModePtr = (): number => {
        const lo  = this.bus.read(this.pc++);
        const hi  = this.bus.read(this.pc++);
        const ptr = (hi << 8) | lo;

        // Quirky 6502 Hardware Bug Wrap-around emulation:
        // If the pointer falls on a page boundary (e.g., $00FF), fetching the
        // high byte rolls over to $0000 instead of crossing to $0100 due to
        // hardware constraints.
        if ((ptr & 0x00FF) === 0x00FF) {
            const targetLo       = this.bus.read(ptr);
            const targetHi       = this.bus.read(ptr & 0xFF00);
            this.absoluteAddress = (targetHi << 8) | targetLo;
        } else {
            const targetLo       = this.bus.read(ptr);
            const targetHi       = this.bus.read(ptr + 1);
            this.absoluteAddress = (targetHi << 8) | targetLo;
        }

        return 0;
    };

    /**
     * Zero Page X Mode: Fetches an 8-bit address, adds the X register,
     *                   and wraps around inside the zero page ($0000-$00FF).
     */
    private modeZeroPageX: AddressingModePtr = (): number => {
        this.absoluteAddress = (this.bus.read(this.pc++) + this.x) & 0x00FF;
        return 0;
    };

    /**
     * Zero Page Y Mode: Fetches an 8-bit address, adds the Y register,
     *                   and wraps around strictly inside the zero page
     *                   ($0000-$00FF).
     */
    private modeZeroPageY: AddressingModePtr = (): number => {
        this.absoluteAddress = (this.bus.read(this.pc++) + this.y) & 0x00FF;
        return 0;
    };

    /**
     * Absolute X Mode: Adds the X register to a full 16-bit absolute address.
     *
     * Returns 1 extra cycle if a 256-byte page boundary is crossed.
     */
    private modeAbsoluteX: AddressingModePtr = (): number => {
        const lo: number   = this.bus.read(this.pc++);
        const hi: number   = this.bus.read(this.pc++);
        const base: number = (hi << 8) | lo;

        this.absoluteAddress = (base + this.x) & 0xFFFF;

        // Check if page boundary was crossed
        return (base & 0xFF00) !== (this.absoluteAddress & 0xFF00) ? 1 : 0;
    };

    /**
     * Absolute Y Mode: Same as Absolute X, but uses the Y register.
     */
    private modeAbsoluteY: AddressingModePtr = (): number => {
        const lo: number   = this.bus.read(this.pc++);
        const hi: number   = this.bus.read(this.pc++);
        const base: number = (hi << 8) | lo;

        this.absoluteAddress = (base + this.y) & 0xFFFF;

        return (base & 0xFF00) !== (this.absoluteAddress & 0xFF00) ? 1 : 0;
    };

    /**
     * Indexed Indirect (Indirect,X): Pre-indexing.
     *
     * Takes an 8-bit zero-page address, adds the X register, and uses that
     * location inside the zero page to find a 16-bit absolute target address.
     */
    private modeIndirectX: AddressingModePtr = (): number => {
        const base: number = this.bus.read(this.pc++);
        const ptr: number  = (base + this.x) & 0xFF;
        const lo: number   = this.bus.read(ptr);
        const hi: number   = this.bus.read((ptr + 1) & 0xFF);

        this.absoluteAddress = (hi << 8) | lo;

        return 0;
    };

    /**
     * Indirect Indexed ((Indirect),Y): Post-indexing.
     *
     * Takes an 8-bit zero-page address to look up a 16-bit address base, then
     * adds the Y register to that base to get the absolute target address.
     *
     * Returns 1 extra cycle if a page boundary is crossed.
     */
    private modeIndirectY: AddressingModePtr = (): number => {
        const ptr: number  = this.bus.read(this.pc++);
        const lo: number   = this.bus.read(ptr);
        const hi: number   = this.bus.read((ptr + 1) & 0xFF);
        const base: number = (hi << 8) | lo;

        this.absoluteAddress = (base + this.y) & 0xFFFF;

        // Check if page boundary was crossed
        return (base & 0xFF00) !== (this.absoluteAddress & 0xFF00) ? 1 : 0;
    };

    // endregion

    // region ---------------------------------------------------- Utilities ---

    /**
     * Utility function to cleanly define the instruction set matrix.
     *
     * @private
     */
    private _defineInstructionSet(specs: CpuInstructionTuple[]): void
    {
        for (const spec of specs) {
            this.instructions[spec[0]] = {
                name:        spec[1],
                cycles:      spec[2],
                operation:   spec[3],
                addressMode: spec[4],
            };
        }
    }

    private _createInstructionLog(instr: CpuInstruction, prevState: State): string
    {
        // 1. Format the addresses (using prevState for the true execution address!)
        const pc   = prevState.pc.toString(16).toUpperCase().padStart(4, '0');
        const addr = this.absoluteAddress.toString(16).toUpperCase().padStart(4, '0');

        // 2. Format registers to always be 2-character hex strings
        const a  = prevState.a.toString(16).toUpperCase().padStart(2, '0');
        const x  = prevState.x.toString(16).toUpperCase().padStart(2, '0');
        const y  = prevState.y.toString(16).toUpperCase().padStart(2, '0');
        const p  = prevState.p.toString(16).toUpperCase().padStart(2, '0');
        const sp = prevState.sp.toString(16).toUpperCase().padStart(2, '0');

        // 3. Construct the instruction segment and pad it for perfect column alignment
        const instructionPart = `${instr.name.padEnd(3, ' ')} $${addr}`;

        // 4. Bring it all together
        return `$${pc} ${instructionPart} | A:${a} X:${x} Y:${y} P:${p} SP:${sp}`;
    }

    // endregion
}

type CpuInstructionTuple = [
    opcode: number,
    name: string,
    cycles: number,
    operation: OperationPtr,
    addressMode: AddressingModePtr,
];

type State = { a: number, x: number, y: number, p: number, sp: number, pc: number };
